import { memo, useEffect, useMemo, useState, type CSSProperties } from "react";
import { isAxiosError } from "axios";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Button,
  GradientText,
  Layout,
  SupportIcon,
} from "lola-framework-ui-test";
import { AuraLayout } from "../components/AuraLayout";
import { parsePhoneNumberFromString, type CountryCode } from "libphonenumber-js";
import { BodyCopy } from "../components/BodyCopy";
import { FlagCircle } from "../components/FlagCircle";
import { Popup } from "../components/Popup";
import { APP_ROUTES, TITLE_COLOR } from "../constants";
import { safeCapture } from "../analytics";
import { useSupportSheet } from "../hooks/useSupportSheet";
import { useRouteWithToken } from "../hooks/useRouteWithToken";
import { saveContactPhone } from "../services/api";
import { useSessionStore } from "../store/useSessionStore";
import { useStore } from "../store/globalState";
import {
  dialCodeForCountry,
  formatNationalInput,
  nationalInputAfterEdit,
  fundRequestPhoneRoleCountry,
  initialPhoneCountry,
  isNationalPhoneValid,
  phoneCountryCodes,
  phoneInputPlaceholder,
} from "../utils/phonePlaceholder";
import { shouldPromptPhone, skipPhoneForLink } from "../utils/phoneGate";
import { ensureSmsConsent } from "../components/notifications/smsConsent";
import { SmsConsentController } from "../components/notifications/SmsConsentController";
import { useNovuInbox } from "../components/notifications/NovuInboxProvider";
import "../styles/phone-capture.css";
import { Navbar } from "../components/Navbar";

interface PhoneLocationState {
  next?: string;
}

const solidColor = (color: string | undefined, fallback: string) =>
  !color || color.includes("gradient(") ? fallback : color;

/**
 * Contact-phone prompt. Callers pass `state.next`, the irreversible step.
 * A stored phone, a disabled partner, or a skip on this link redirects there.
 * `onFinished` keeps the caller mounted (cash deposit) instead of routing.
 */
export const PhoneNumberPage = memo(({ onFinished }: { onFinished?: () => void }) => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const next =
    (location.state as PhoneLocationState | null)?.next ||
    APP_ROUTES.SENDING_MONEY_PAGE;
  const { redirectTo } = useRouteWithToken();
  const inbox = useNovuInbox();
  const { session, updateSession } = useSessionStore();
  const { theme } = useStore();
  const { openSupport, supportSheet } = useSupportSheet();
  const { bodyCopy, dropzoneColors, subtitile, errorColor, background, backgroundBtn, textColorBtn } =
    theme.whiteView;
  const linkId = session.data?.linkId;
  const countries = useMemo(() => phoneCountryCodes(), []);
  const [countryCode, setCountryCode] = useState(() =>
    initialPhoneCountry({
      countries,
      identityCountry: session.data?.user?.country,
      roleCountry: fundRequestPhoneRoleCountry(session.data?.config),
    })
  );
  const [value, setValue] = useState("");
  const [touched, setTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [query, setQuery] = useState("");

  useEffect(() => {
    let cancelled = false;
    const lang = i18n.language?.slice(0, 2) || "en";
    void (async () => {
      if (!shouldPromptPhone(session.data, linkId)) {
        if (linkId) await ensureSmsConsent(linkId, lang);
        if (cancelled) return;
        if (onFinished) onFinished();
        else redirectTo(next, { replace: true });
        return;
      }
      safeCapture("ux.phone_number.screen.view", {
        partner_id: session.data?.partner?.partnerId,
        flow: session.data?.config?.flow || "fund-request",
        language: i18n.language,
      });
    })();
    return () => {
      cancelled = true;
    };
    // Fire once when the prompt is actually shown.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const digits = value.replace(/\D/g, "");
  const valid = isNationalPhoneValid(countryCode, value);
  const showInvalid = touched && digits.length > 0 && !valid;
  const dial = dialCodeForCountry(countryCode);
  const placeholder = phoneInputPlaceholder(countryCode);
  const countryName = countryLabel(countryCode, i18n.language);
  const ink = solidColor(bodyCopy, "#979797");
  const filtered = countries
    .filter((code) => {
      const name = countryLabel(code, i18n.language).toLowerCase();
      const needle = query.trim().toLowerCase();
      return !needle || name.includes(needle) || code.toLowerCase().includes(needle) || dialCodeForCountry(code).includes(needle);
    })
    .sort((a, b) =>
      countryLabel(a, i18n.language).localeCompare(countryLabel(b, i18n.language), i18n.language),
    );

  const finish = () => {
    if (onFinished) {
      onFinished();
      return;
    }
    redirectTo(next, { replace: true });
  };

  const onSkip = () => {
    if (linkId) skipPhoneForLink(linkId);
    safeCapture("ui.phone_number.skip_button.click", {
      partner_id: session.data?.partner?.partnerId,
      flow: session.data?.config?.flow || "fund-request",
      language: i18n.language,
    });
    finish();
  };

  const onContinue = async () => {
    setTouched(true);
    if (!valid || !linkId || saving) return;
    const parsed = parsePhoneNumberFromString(digits, countryCode as CountryCode);
    if (!parsed?.isValid()) return;
    setSaving(true);
    setSaveError(false);
    try {
      await saveContactPhone(linkId, parsed.number, i18n.language);
      if (session.data) {
        updateSession({
          ...session.data,
          user: { ...session.data.user, hasPhoneNumber: true },
        });
      }
      safeCapture("ux.phone_number.saved", {
        partner_id: session.data?.partner?.partnerId,
        flow: session.data?.config?.flow || "fund-request",
        phone_country: parsed.country,
        language: i18n.language,
      });
      await ensureSmsConsent(linkId, i18n.language?.slice(0, 2) || "en");
      finish();
    } catch (error) {
      if (isAxiosError(error) && error.response?.status === 404) {
        finish();
        return;
      }
      setSaveError(true);
      setSaving(false);
      safeCapture("ux.phone_number.save.error", {
        partner_id: session.data?.partner?.partnerId,
        flow: session.data?.config?.flow || "fund-request",
        phone_country: parsed.country,
        language: i18n.language,
        error_code: isAxiosError(error) ? String(error.response?.status ?? "NETWORK") : "UNKNOWN",
      });
    }
  };

  const phoneVars = {
    "--phone-accent": dropzoneColors[0],
    "--phone-title": bodyCopy,
    "--phone-body": bodyCopy,
    "--phone-ink": ink,
    "--phone-field-rest": solidColor(subtitile, "#979797"),
    "--phone-field-active": ink,
    "--phone-field-error": solidColor(errorColor, "#dc251c"),
    "--phone-field-hint": "var(--muted-foreground, #61616b)",
  } as CSSProperties;

  return (
    <>
      <AuraLayout colorConfig={theme.whiteView} className="phone-capture-page need-help-header">
        <Layout.Header>
          <Navbar
            title={t("common.needHelp")}
            align="right"
            noBackButton
            onIconClick={() => openSupport()}
            icon={<SupportIcon size={35} colors={dropzoneColors} />}
          />
        </Layout.Header>
        <Layout.Content>
          <section className="phone-capture-card" style={phoneVars}>
            <p className="phone-capture-heading">{t("phone.heading")}</p>
            <label className="phone-capture-label" htmlFor="phone-capture-input">
              {t("phone.label")}
            </label>
            <div
              className={[
                "phone-capture-field",
                digits.length > 0 ? "is-filled" : "",
                showInvalid || saveError ? "is-invalid" : "",
              ]
                .filter(Boolean)
                .join(" ")}
            >
              <button
                type="button"
                className="phone-capture-country"
                disabled={saving}
                aria-label={t("phone.country.change", { country: countryName })}
                onClick={() => setPickerOpen(true)}
              >
                <FlagCircle iso2={countryCode.toLowerCase()} size={28} />
                <span>{dial}</span>
              </button>
              <input
                id="phone-capture-input"
                className="phone-capture-input"
                inputMode="tel"
                autoComplete="tel"
                placeholder={placeholder}
                aria-invalid={showInvalid || saveError}
                readOnly={saving}
                value={value}
                onChange={(event) =>
                  setValue(nationalInputAfterEdit(countryCode, value, event.target.value, event.target.selectionStart))
                }
                onBlur={() => setTouched(true)}
              />
            </div>
            {showInvalid ? <p className="phone-capture-error">{t("phone.invalid")}</p> : null}
            {saveError ? <p className="phone-capture-error">{t("phone.saveError")}</p> : null}
            <BodyCopy className="phone-capture-note" textColor={bodyCopy}>
              {t("phone.description")}
            </BodyCopy>
          </section>
        </Layout.Content>
        <Layout.Footer>
          <section className="footer-section phone-capture-actions">
            <Button
              type="button"
              size="large"
              variant="outline"
              background={backgroundBtn}
              color={TITLE_COLOR}
              style={{ backgroundColor: background }}
              disabled={saving}
              onClick={onSkip}
            >
              {t("phone.skip")}
            </Button>
            <Button
              size="large"
              background={backgroundBtn}
              color={textColorBtn}
              disabled={!valid || saving}
              onClick={() => void onContinue()}
            >
              {saving ? t("phone.saving") : t("phone.continue")}
            </Button>
          </section>
        </Layout.Footer>
      </AuraLayout>
      {supportSheet}
      {inbox ? null : (
        <SmsConsentController linkId={linkId ?? null} onDone={() => undefined} />
      )}
      <Popup visible={pickerOpen} onClose={() => setPickerOpen(false)}>
        <section
          className="phone-capture-picker"
          style={{ ...phoneVars, "--phone-option-ring": backgroundBtn } as CSSProperties}
        >
          <GradientText as="h2" textColor={bodyCopy} className="phone-capture-picker-title h2">
            {t("phone.country.select")}
          </GradientText>
          <input
            type="text"
            className="phone-capture-picker-search"
            value={query}
            placeholder={t("phone.country.search")}
            aria-label={t("phone.country.search")}
            autoComplete="off"
            onChange={(event) => setQuery(event.target.value)}
          />
          <ul className="phone-capture-picker-list">
            {filtered.length === 0 ? (
              <li className="phone-capture-picker-empty">{t("phone.country.empty")}</li>
            ) : null}
            {filtered.map((code) => (
              <li key={code}>
                <button
                  type="button"
                  className={`phone-capture-picker-option${code === countryCode ? " is-selected" : ""}`}
                  aria-pressed={code === countryCode}
                  onClick={() => {
                    setCountryCode(code);
                    setValue((current) => formatNationalInput(code, current));
                    setPickerOpen(false);
                    setQuery("");
                  }}
                >
                  <span className="phone-capture-picker-flag">
                    <FlagCircle iso2={code.toLowerCase()} size={24} />
                  </span>
                  <span className="phone-capture-picker-name">
                    {countryLabel(code, i18n.language)}
                  </span>
                  <span className="phone-capture-picker-dial">{dialCodeForCountry(code)}</span>
                </button>
              </li>
            ))}
          </ul>
        </section>
      </Popup>
    </>
  );
});

function countryLabel(code: string, language: string): string {
  try {
    return new Intl.DisplayNames([language || "en"], { type: "region" }).of(code) || code;
  } catch {
    return code;
  }
}
